import { z } from 'zod';
import { WorkItemStatusSchema } from './domain.js';
export const MEEGO_ORIGIN = 'https://project.feishu.cn';
export const MEEGO_PROGRESS_PATH = '/api/v1/meego/progress';
export const MEEGO_CONNECTION_PATH = '/api/v1/meego/connections';
export const MEEGO_PUBLIC_PROGRESS_PATH = '/api/v1/meego/public-progress';
/** The plugin button starts a task here, with the clicker's Meego auth code instead of a login. */
export const MEEGO_PLUGIN_START_PATH = '/api/v1/meego/plugin-starts';
export const MeegoPluginStartInputSchema = z
    .object({ code: z.string().trim().min(1).max(512), url: z.string().url().max(2_048) })
    .strict();
export const MeegoPluginStartResultSchema = z.object({
    workItem: z.object({ id: z.string(), key: z.string(), title: z.string() }),
    /** Whether a defect still 未开始 was moved to 进行中 as the clicker. */
    transition: z.enum(['MOVED', 'SKIPPED', 'FAILED']),
});
/** The TCode column of a Meego table: the latest task of one item, from TCode's own records. */
export const MEEGO_CELL_STATUS_PATH = '/api/v1/meego/cell-status';
export const MeegoCellStatusInputSchema = z
    .object({
    workObjectId: z.enum(['issue', 'story']),
    workItemId: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
})
    .strict();
export const MeegoCellStatusSchema = z.object({
    count: z.number().int().nonnegative(),
    latest: z.object({ key: z.string(), status: WorkItemStatusSchema }).nullable(),
});
export const MeegoRestartInputSchema = z
    .object({
    workflowRunId: z.string().uuid(),
    requestId: z.string().uuid(),
    note: z.string().trim().max(20_000).optional().default(''),
})
    .strict();
export const MeegoConnectInputSchema = z
    .object({
    url: z
        .url()
        .max(2_048)
        .refine((value) => {
        const url = new URL(value);
        return url.origin === MEEGO_ORIGIN && !url.username && !url.password;
    }),
})
    .strict();
export const MeegoSessionSchema = z
    .object({
    token: z.string().min(1).max(4_096),
    expiresAt: z.number().int().positive(),
})
    .strict();
export const MeegoProgressInputSchema = MeegoSessionSchema.pick({ token: true });
export const MeegoPublicProgressInputSchema = MeegoConnectInputSchema;
const ConnectionProofSchema = z.string().regex(/^[a-f0-9]{64}$/);
export const MeegoAuthorizeConnectionSchema = z
    .object({ channel: z.string().uuid(), challenge: ConnectionProofSchema })
    .strict();
export const MeegoSessionInputSchema = MeegoConnectInputSchema.extend({
    connection: MeegoAuthorizeConnectionSchema.optional(),
});
export const MeegoConnectionPollInputSchema = z
    .object({ channel: z.string().uuid(), verifier: ConnectionProofSchema })
    .strict();
export const MeegoConnectionResultSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('PENDING') }).strict(),
    z.object({ status: z.literal('CONNECTED'), session: MeegoSessionSchema }).strict(),
]);
export const MeegoConnectionMessageSchema = MeegoSessionSchema.extend({
    type: z.literal('tcode:meego-connected'),
    channel: z.string().uuid(),
}).strict();
export const MeegoProgressSchema = z.object({
    workItem: z
        .object({
        id: z.string().uuid(),
        workflowRunId: z.string().uuid().nullable().optional(),
        key: z.string(),
        title: z.string(),
        status: WorkItemStatusSchema,
        updatedAt: z.string(),
        currentStage: z.string().nullable(),
        notice: z.string().nullable(),
        stages: z.array(z.object({ key: z.string(), name: z.string(), status: z.string() })),
        links: z.array(z.object({
            kind: z.enum(['PULL_REQUEST', 'TEST_DEPLOYMENT', 'DOCUMENT']),
            name: z.string(),
            url: z.url().refine((value) => new URL(value).protocol === 'https:'),
        })),
    })
        .nullable(),
    workItems: z
        .array(z.object({
        id: z.string().uuid(),
        workflowRunId: z.string().uuid().nullable().optional(),
        key: z.string(),
        title: z.string(),
        status: WorkItemStatusSchema,
        updatedAt: z.string(),
        currentStage: z.string().nullable(),
        notice: z.string().nullable(),
        stages: z.array(z.object({ key: z.string(), name: z.string(), status: z.string() })),
        links: z.array(z.object({
            kind: z.enum(['PULL_REQUEST', 'TEST_DEPLOYMENT', 'DOCUMENT']),
            name: z.string(),
            url: z.url().refine((value) => new URL(value).protocol === 'https:'),
        })),
    }))
        .default([]),
    taskCount: z.number().int().min(0).default(0),
});
//# sourceMappingURL=meego.js.map